/**
 * Business errors raised by SQL (`private.fail(code, detail, hint)`) arrive from
 * PostgREST as `{ code: 'P0001', message: <code>, details: <ru text>, hint }`.
 */
const MESSAGES: Record<string, string> = {
  tenant_not_found: 'Студия не найдена.',
  service_not_found: 'Услуга недоступна.',
  slot_unavailable: 'Это время уже заняли. Выберите другое.',
  invalid_customer: 'Проверьте контактные данные.',
  invalid_request: 'Некорректный запрос.',
  invalid_token: 'Ссылка на запись недействительна.',
  idempotency_conflict: 'Запрос уже использован. Обновите страницу.',
  booking_not_found: 'Запись не найдена.',
  cancel_window_closed: 'Отменить запись онлайн уже нельзя. Позвоните в студию.',
  invalid_status: 'Действие недоступно для записи в этом статусе.',
  rate_limited: 'Слишком много запросов. Попробуйте через несколько минут.',
  not_authenticated: 'Войдите в кабинет.',
  forbidden: 'Нет доступа.',
  invalid_period: 'Неверный период.',
  refund_exceeds_paid: 'Возврат больше полученной суммы.',
  not_ready: 'Студия ещё не готова к приёму записей.',
  network: 'Нет соединения. Проверьте интернет и повторите.',
  unknown: 'Что-то пошло не так. Повторите попытку.',
};

const NON_RETRYABLE = new Set([
  'tenant_not_found',
  'service_not_found',
  'slot_unavailable',
  'invalid_customer',
  'invalid_request',
  'invalid_token',
  'idempotency_conflict',
  'booking_not_found',
  'cancel_window_closed',
  'invalid_status',
  'rate_limited',
  'not_authenticated',
  'forbidden',
  'invalid_period',
  'refund_exceeds_paid',
  'not_ready',
]);

export class ApiError extends Error {
  readonly code: string;
  readonly detail: string | null;
  readonly field: string | null;

  constructor(code: string, detail?: string | null, field?: string | null) {
    super(detail || MESSAGES[code] || MESSAGES.unknown);
    this.name = 'ApiError';
    this.code = code;
    this.detail = detail ?? null;
    this.field = field ?? null;
  }

  get retryable(): boolean {
    return !NON_RETRYABLE.has(this.code);
  }

  /** Text that is safe and useful to show to a person. */
  get userMessage(): string {
    return this.detail || MESSAGES[this.code] || MESSAGES.unknown || 'Ошибка';
  }
}

interface PostgrestLikeError {
  code?: string;
  message?: string;
  details?: string | null;
  hint?: string | null;
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  if (error && typeof error === 'object') {
    const e = error as PostgrestLikeError & { name?: string };
    if (e.code === 'P0001' && e.message) {
      return new ApiError(e.message, e.details || null, e.hint || null);
    }
    if (e.code === '42501') return new ApiError('forbidden');
    if (e.code === 'PGRST301' || e.code === '401') return new ApiError('not_authenticated');
    if (e.name === 'TypeError' || /fetch|network/i.test(e.message ?? '')) return new ApiError('network');
    if (e.message && MESSAGES[e.message]) return new ApiError(e.message);
  }
  return new ApiError('unknown');
}

export function errorMessage(error: unknown): string {
  return toApiError(error).userMessage;
}
